import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  pdf,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";

import {
  nutritionItems,
  mealLocationItems,
  feedingAssistanceItems,
  hydrationItems,
  assistiveDevicesItems,
  visionItems,
  speechItems,
  hearingItems,
  toileting as toiletingItems,
  brushingTeeth as brushingTeethItems,
  bedCheck as bedCheckItems,
  skinCare as skinCareItems,
  nutritionAndHydration as nutritionAndHydrationItems,
  undergarments as undergarmentItems,
  dressing as dressingItems,
  grooming as groomingItems,
  shower as showerItems,
  other as otherItems,
} from "../ServicePlanComponents/ServicePlanSteps/AssessmentStep";

import {
  safetyMonitoringItems,
  cognitiveActivitiesItems,
  coordinationOfCareItems,
  familyRepCommunicationItems,
  medicationAdministrationItems,
  cognitiveStatusItems,
  mobilityAmbulationTransferItems,
  nursingMedicalInstructionsItems,
  conditionsRequiringCaregiversItems,
} from "../ServicePlanComponents/ServicePlanSteps/CareAndSafetyStep";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const str = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object")
    return val.field || val.name || val.label || val.value || "";
  return String(val);
};

// ─── StyleSheet ───────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  // ── Cover page ──
  coverPage: {
    padding: 0,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#262626",
  },
  coverPageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 14,
    paddingLeft: 50,
    paddingRight: 50,
    paddingBottom: 12,
  },
  coverAccentBar: {
    backgroundColor: "#FFBA45",
    height: 14,
  },
  coverBody: {
    padding: "44 50",
  },
  coverLogoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 52,
  },
  coverLogo: {
    width: 40,
    height: 40,
    objectFit: "contain",
  },
  coverBusinessName: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
    textAlign: "right",
  },
  coverBusinessSub: {
    fontSize: 8.5,
    color: "#6B7280",
    marginTop: 2,
    textAlign: "right",
  },
  coverPlanTypeLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#FFBA45",
    letterSpacing: 2,
    marginBottom: 8,
  },
  coverMainTitle: {
    fontSize: 50,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
    color: "#171717",
    lineHeight: 1.1,
    marginBottom: 14,
    maxWidth: 320,
  },
  coverSubtitle: {
    fontSize: 10,
    color: "#525252",
    lineHeight: 1.6,
    maxWidth: 340,
    marginBottom: 36,
  },

  // Resident summary card on cover
  resCard: {
    border: "1 solid #E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
  },
  resCardTop: {
    padding: "12 16",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#FAFAFA",
  },
  resCardTopLabel: {
    fontSize: 7,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    marginBottom: 3,
  },
  resCardTopName: {
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },
  resCardBadge: {
    backgroundColor: "#FEF3C7",
    borderRadius: 10,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 10,
    paddingRight: 10,
  },
  resCardBadgeText: {
    fontSize: 8,
    color: "#92400E",
  },
  resCardGrid1: {
    flexDirection: "row",
    borderTop: "1 solid #E5E7EB",
  },
  resCardGrid2: {
    flexDirection: "row",
    borderTop: "1 solid #E5E7EB",
  },
  resCardCell: {
    flex: 1,
    padding: "9 14",
    borderRight: "1 solid #E5E7EB",
  },
  resCardCellLast: {
    flex: 1,
    padding: "9 14",
  },
  resCardCellLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  resCardCellValue: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },

  // Cover footer
  coverFooter: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  coverFooterText: {
    fontSize: 7.5,
    color: "#9CA3AF",
  },

  // ── Content page ──
  page: {
    paddingTop: 44,
    paddingBottom: 44,
    paddingLeft: 40,
    paddingRight: 40,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#262626",
  },

  // Fixed page header
  pageHeader: {
    position: "absolute",
    top: 12,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "0.5 solid #E5E7EB",
    paddingBottom: 5,
  },
  pageHeaderText: {
    fontSize: 7,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
  },

  // Fixed page footer
  pageFooter: {
    position: "absolute",
    bottom: 12,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "0.5 solid #E5E7EB",
    paddingTop: 5,
  },
  pageFooterText: {
    fontSize: 7,
    color: "#9CA3AF",
  },

  // ── Section heading (01 Resident & Intake Data) ──
  secHeading: {
    flexDirection: "row",
    alignItems: "baseline",
    borderBottom: "1.5 solid #171717",
    paddingBottom: 7,
    marginBottom: 14,
    marginTop: 16,
  },
  secNum: {
    fontSize: 10,
    color: "#F59E0B",
    fontFamily: "Helvetica-Bold",
    marginRight: 6,
  },
  secTitle: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },

  // ── Subsection dot header ──
  dotHead: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 7,
    marginTop: 10,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#F59E0B",
    marginRight: 6,
    flexShrink: 0,
  },
  dotHeadText: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: "#374151",
    letterSpacing: 1.2,
  },

  // ── Simple field (label + bold value) ──
  fieldGroup: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  fieldValue: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },
  fieldPlaceholder: {
    fontSize: 9.5,
    color: "#D1D5DB",
  },
  fieldRow: {
    flexDirection: "row",
    gap: 14,
    marginBottom: 10,
  },

  // ── Vital signs table ──
  vitalTable: {
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 6,
    marginBottom: 10,
  },
  vitalRowTop: {
    flexDirection: "row",
    borderBottom: "1 solid #E5E7EB",
  },
  vitalRowBottom: {
    flexDirection: "row",
  },
  vitalCell: {
    flex: 1,
    padding: "8 12",
    borderRight: "1 solid #E5E7EB",
  },
  vitalCellEnd: {
    flex: 1,
    padding: "8 12",
  },
  vitalCellGray: {
    flex: 2,
    padding: "8 12",
    backgroundColor: "#F9FAFB",
  },
  vitalCellLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  vitalCellValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },

  // ── Diagnosis / Allergies row ──
  diagAllergyRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 10,
  },
  diagBlock: {
    flex: 3,
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: "10 14",
  },
  diagBlockLabel: {
    fontSize: 7,
    color: "#374151",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  diagDotRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3,
  },
  diagDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#F59E0B",
    marginRight: 6,
    marginTop: 2.5,
    flexShrink: 0,
  },
  diagItemText: {
    fontSize: 8.5,
    color: "#374151",
    flex: 1,
  },
  allergyBlock: {
    flex: 2,
    backgroundColor: "#FEF2F2",
    border: "1 solid #FEE2E2",
    borderRadius: 6,
    padding: "10 14",
  },
  allergyBlockLabel: {
    fontSize: 7,
    color: "#EF4444",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  allergyItemText: {
    fontSize: 8.5,
    color: "#EF4444",
    marginBottom: 2,
  },

  // ── Clinical summary / Notes block (left amber border) ──
  noteBlock: {
    borderLeft: "3 solid #F59E0B",
    paddingLeft: 10,
    marginTop: 8,
    marginBottom: 10,
  },
  noteBlockLabel: {
    fontSize: 7,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  noteBlockText: {
    fontSize: 9,
    color: "#374151",
    lineHeight: 1.5,
  },

  // ── Contact cards ──
  contactRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  contactCard: {
    flex: 1,
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: "10 14",
  },
  contactRoleLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  contactName: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
    marginBottom: 2,
  },
  contactDetail: {
    fontSize: 8.5,
    color: "#6B7280",
    marginBottom: 1,
  },

  // ── Checkbox section ──
  cbSection: {
    marginBottom: 6,
  },
  cbColumns: {
    flexDirection: "row",
    gap: 10,
  },
  cbCol: {
    flex: 1,
  },
  cbItemRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 5,
  },
  cbSubItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
    marginLeft: 14,
  },
  cbTextBox: {
    backgroundColor: "#F9FAFB",
    border: "0.5 solid #E5E7EB",
    borderRadius: 3,
    padding: "3 6",
    marginBottom: 4,
    marginLeft: 16,
  },
  cbTextValue: {
    fontSize: 7.5,
    color: "#4B5563",
  },
  cbSubTextIndent: {
    marginLeft: 28,
  },
  cbBox: {
    minWidth: 11,
    maxWidth: 11,
    minHeight: 11,
    maxHeight: 11,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#FFFFFF",
    marginRight: 5,
    flexShrink: 0,
    marginTop: 0.5,
  },
  cbBoxChecked: {
    minWidth: 11,
    maxWidth: 11,
    minHeight: 11,
    maxHeight: 11,
    borderRadius: 2,
    backgroundColor: "#F59E0B",
    marginRight: 5,
    flexShrink: 0,
    marginTop: 0.5,
    alignItems: "center",
    justifyContent: "center",
  },
  checkmark: {
    width: "70%",
    height: "70%",
    objectFit: "contain",
  },
  cbLabel: {
    fontSize: 8.5,
    color: "#374151",
    flex: 1,
    lineHeight: 1.3,
  },
  cbLabelChecked: {
    fontSize: 8.5,
    color: "#171717",
    fontFamily: "Helvetica-Bold",
    flex: 1,
    lineHeight: 1.3,
  },

  // ── Card-based checkbox section ──
  cbCard: {
    border: "1 solid #E5E7EB",
    borderRadius: 8,
    padding: "10 12",
    marginBottom: 8,
  },
  cbCardHalf: {
    border: "1 solid #E5E7EB",
    borderRadius: 8,
    padding: "10 12",
    marginBottom: 8,
    flex: 1,
  },
  cbCardTitle: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: "#6B7280",
    letterSpacing: 1,
    marginBottom: 8,
  },
  cbCardRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 0,
  },
  notesCardLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 6,
  },
  notesCardBox: {
    border: "0.5 solid #D1D5DB",
    borderRadius: 6,
    padding: "8 10",
    minHeight: 36,
    marginBottom: 10,
  },
  notesCardText: {
    fontSize: 9,
    color: "#374151",
    lineHeight: 1.5,
  },

  // ── Notify physician box ──
  notifyBox: {
    backgroundColor: "#FFFBEB",
    border: "1 solid #FDE68A",
    borderRadius: 6,
    padding: "10 14",
    marginTop: 10,
  },
  notifyBoxLabel: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: "#B45309",
    letterSpacing: 1,
    marginBottom: 8,
  },
  notifyGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  notifyItem: {
    width: "50%",
    flexDirection: "row",
    alignItems: "flex-start",
    paddingRight: 8,
    marginBottom: 4,
  },
  notifyBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#F59E0B",
    marginRight: 6,
    marginTop: 2.5,
    flexShrink: 0,
  },
  notifyText: {
    fontSize: 8,
    color: "#B45309",
    flex: 1,
    lineHeight: 1.3,
  },

  // ── Review & Signatures ──
  sigParagraph: {
    fontSize: 9,
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: 20,
  },
  reviewDatesRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 20,
  },
  reviewDateCard: {
    flex: 1,
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: "10 14",
  },
  reviewDateLabel: {
    fontSize: 7,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  reviewDateValue: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#171717",
  },
  sigBlock: {
    border: "1 solid #E5E7EB",
    borderRadius: 6,
    padding: "14 16",
    marginBottom: 10,
  },
  sigBlockRole: {
    fontSize: 10,
    color: "#9CA3AF",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 14,
  },
  sigFieldsRow: {
    flexDirection: "row",
    gap: 20,
    alignItems: "flex-end",
  },
  sigFieldGroup: {
    flex: 1,
  },
  sigFieldGroupWide: {
    flex: 1.5,
  },
  sigLine: {
    borderBottom: "1 solid #D1D5DB",
    minHeight: 28,
    paddingBottom: 2,
  },
  sigValue: {
    fontSize: 9,
    color: "#171717",
  },
  sigSignBox: {
    borderBottom: "1 solid #D1D5DB",
    minHeight: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  sigImage: {
    maxHeight: 32,
    objectFit: "contain",
  },
  sigTextSign: {
    fontSize: 18,
    color: "#262626",
    fontFamily: "Island Moments",
  },
  sigFieldLabel: {
    fontSize: 6.5,
    color: "#9CA3AF",
    marginTop: 3,
  },
  addressTextParent:{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  }
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const PDFPageHeader = ({ residentName, facilityName }) => (
  <View style={S.pageHeader} fixed>
    <Text style={S.pageHeaderText}>
      {residentName ? residentName.toUpperCase() + " · SERVICE PLAN" : "SERVICE PLAN"}
    </Text>
    <Text style={S.pageHeaderText}>
      {facilityName ? facilityName.toUpperCase() : ""}
    </Text>
  </View>
);

const PDFPageFooter = ({ facilityName, facilityAddress }) => {
  const left = [facilityName, facilityAddress]
    .filter(Boolean)
    .join(" · ")
    .toUpperCase();
  return (
    <View style={S.pageFooter} fixed>
      <Text style={S.pageFooterText}>{left}</Text>
      <Text style={S.pageFooterText}>CONFIDENTIAL — PHI</Text>
    </View>
  );
};

const PDFSectionHeading = ({ num, title }) => (
  <View style={S.secHeading}>
    <Text style={S.secNum}>{num}</Text>
    <Text style={S.secTitle}>{title}</Text>
  </View>
);

const PDFDotHead = ({ title }) => (
  <View style={S.dotHead}>
    <View style={S.dot} />
    <Text style={S.dotHeadText}>{title.toUpperCase()}</Text>
  </View>
);

const PDFField = ({ label, value }) => (
  <View style={S.fieldGroup}>
    <Text style={S.fieldLabel}>{label ? label.toUpperCase() : ""}</Text>
    <Text style={str(value) ? S.fieldValue : S.fieldPlaceholder}>
      {str(value) || "—"}
    </Text>
  </View>
);

const PDFCheckboxSection = ({
  title,
  itemsDef = [],
  selected = [],
  halfWidth = false,
  columns = 2,
}) => {
  const selectedMap = {};
  const selectedSubMap = {};

  (selected || []).forEach((s) => {
    if (!s?.name) return;
    selectedMap[s.name] = s;
    if (s?.subItems?.length) {
      selectedSubMap[s.name] = new Set(s.subItems.map((sub) => sub.name));
    }
  });

  const items = itemsDef || [];
  const cols =
    columns === 1
      ? [items]
      : [items.filter((_, i) => i % 2 === 0), items.filter((_, i) => i % 2 === 1)];

  const renderItem = (item, idx) => {
    const sel = selectedMap[item.name];
    const isChecked = !!sel;
    const hasSubDef = item.subItems?.length > 0;
    const text = sel?.text;

    return (
      <View key={idx}>
        <View style={S.cbItemRow}>
          <View style={isChecked ? S.cbBoxChecked : S.cbBox}>
            {isChecked && (
              <Image
                src="/assets/images/check-icon.png"
                style={S.checkmark}
              />
            )}
          </View>
          <Text style={isChecked ? S.cbLabelChecked : S.cbLabel}>
            {item.name}
          </Text>
        </View>

        {isChecked && text ? (
          <View style={S.cbTextBox}>
            <Text style={S.cbTextValue}>{text}</Text>
          </View>
        ) : null}

        {isChecked && hasSubDef
          ? item.subItems.map((sub, j) => {
              const subChecked =
                selectedSubMap[item.name]?.has(sub.name) || false;
              const subText = sel?.subItems?.find(
                (s) => s.name === sub.name,
              )?.text;
              return (
                <View key={j}>
                  <View style={S.cbSubItem}>
                    <View style={subChecked ? S.cbBoxChecked : S.cbBox}>
                      {subChecked && (
                        <Image
                          src="/assets/images/check-icon.png"
                          style={S.checkmark}
                        />
                      )}
                    </View>
                    <Text style={subChecked ? S.cbLabelChecked : S.cbLabel}>
                      {sub.name}
                    </Text>
                  </View>
                  {subChecked && subText ? (
                    <View style={[S.cbTextBox, S.cbSubTextIndent]}>
                      <Text style={S.cbTextValue}>{subText}</Text>
                    </View>
                  ) : null}
                </View>
              );
            })
          : null}
      </View>
    );
  };

  return (
    <View style={halfWidth ? S.cbCardHalf : S.cbCard}>
      <Text style={S.cbCardTitle}>{title.toUpperCase()}</Text>
      <View style={S.cbColumns}>
        {cols.map((col, ci) => (
          <View key={ci} style={S.cbCol}>
            {col.map(renderItem)}
          </View>
        ))}
      </View>
    </View>
  );
};

const PDFSignatureRow = ({ sig }) => (
  <View style={S.sigBlock}>
    <View style={{ flexDirection: "row", minHeight: 72 }}>
      {/* Left: role at top, signature line at bottom */}
      <View
        style={{
          width: "36%",
          paddingRight: 20,
          justifyContent: "space-between",
        }}
      >
        <Text style={S.sigBlockRole}>{sig?.position || ""}</Text>
        <View>
          <View
            style={{
              borderBottom: "1 solid #1C1917",
              minHeight: 32,
              justifyContent: "flex-end",
            }}
          >
            {sig?.signature ? (
              <Image  src={{
                uri: sig.signature,
                method: "GET",
                headers: { "Cache-Control": "no-cache" },
                body: null,
              }} style={S.sigImage} />
            ) : sig?.signatureImage ? (
              <Image  src={{
                uri: sig.signatureImage,
                method: "GET",
                headers: { "Cache-Control": "no-cache" },
                body: null,
              }}style={S.sigImage} />
            ) : sig?.signatureText ? (
              <Text style={S.sigTextSign}>{sig.signatureText}</Text>
            ) : null}
          </View>
          <Text style={{ fontSize: 6.5, color: "#9CA3AF", marginTop: 3 }}>
            Signature
          </Text>
        </View>
      </View>

      {/* Right: name directly above Printed Name line, Date alongside — all bottom-aligned */}
      <View style={{ flex: 1, flexDirection: "row", gap: 16, alignItems: "flex-end" }}>
        <View style={{ flex: 1.5 }}>
          <Text
            style={{
              fontSize: 12,
              fontFamily: "Helvetica-Bold",
              color: "#1C1917",
              marginBottom: 8,
            }}
          >
            {sig?.fullName || ""}
          </Text>
          <View style={{ borderBottom: "1 solid #D1D5DB" }} />
          <Text style={{ fontSize: 6.5, color: "#9CA3AF", marginTop: 3 }}>
            Printed Name
          </Text>
        </View>
        <View style={{ flex: 1 }}>
          <View style={{ borderBottom: "1 solid #D1D5DB", minHeight: 20 }}>
            <Text style={{ fontSize: 9, color: "#171717" }}>
              {sig?.date ? moment(sig.date).format("MM/DD/YYYY") : ""}
            </Text>
          </View>
          <Text style={{ fontSize: 6.5, color: "#9CA3AF", marginTop: 3 }}>
            Date
          </Text>
        </View>
      </View>
    </View>
  </View>
);

// ─── Main Document ────────────────────────────────────────────────────────────

export const ServicePlanDocument = ({
  planPayload,
  taskTypeDetails,
  businessName,
  email,
}) => {
  console.log("🚀 ~ ServicePlanDocument ~ planPayload:", planPayload)
  const data = planPayload?.servicePlanData || {};
  const clientData = data?.clientData || {};
  const assessment = data?.assessment || {};
  const adl = data?.activitiesOfDailyLiving || {};
  const careAndSafety = data?.careAndSafety || {};
  const signature = data?.signature || [];

  const dateRange =
    planPayload?.startDate && planPayload?.endDate
      ? `${moment(planPayload.startDate).format("MM/DD/YYYY")} – ${moment(planPayload.endDate).format("MM/DD/YYYY")}`
      : "—";

  const residentName = [clientData?.firstName, clientData?.lastName]
    .filter(Boolean)
    .join(" ");

  const facilityName = planPayload?.facility?.name
    ? planPayload.facility.name
    : str(planPayload?.facility) || businessName || "";
  const facilityAddress = planPayload?.facility?.address || "";

  const buildAdlSelected = (fieldsDone = [], subTask) => {
    if (!fieldsDone.length || !subTask?.fields?.length) return [];
    return subTask.fields
      .filter((field) => fieldsDone.some((done) => done.field === field._id))
      .map((field) => {
        const doneItem = fieldsDone.find((done) => done.field === field._id);
        return { name: field.name, text: doneItem?.textField || "" };
      });
  };

  const buildAdlItemsDef = (subTask) => {
    if (!subTask?.fields) return [];
    return subTask.fields.map((field) => ({ name: field?.name }));
  };

  Font.register({
    family: "Island Moments",
    fonts: [{ src: "/assets/fonts/IslandMoments-Regular.ttf" }],
  });

  const selectedConditions = careAndSafety?.conditionsRequiringCaregivers || [];

  return (
    <Document>
      {/* ══════ Cover Page ══════ */}
      <Page size="A4" style={S.coverPage}>
        <View style={S.coverAccentBar} />
        <View style={S.coverBody}>
          <View style={S.coverLogoRow}>
            <Image src="/assets/images/logo2.png" style={S.coverLogo} />
            <View style={S.addressTextParent}>
              <Text style={S.coverBusinessName}>
                {planPayload?.facility?.name
                  ? planPayload.facility.name
                  : str(planPayload?.facility) || businessName || ""}
              </Text>
              {planPayload?.facility?.address ? (
                <Text style={S.coverBusinessSub}>
                  {planPayload.facility.address}
                </Text>
              ) : null}
            </View>
          </View>

          <Text style={S.coverPlanTypeLabel}>INITIAL SERVICE PLAN</Text>
          <Text style={S.coverMainTitle}>{"Resident\nService Plan"}</Text>
          <Text style={S.coverSubtitle}>
            A coordinated plan of care documenting assessed needs, daily living
            support, medication administration, and safety protocols for the
            resident named below.
          </Text>

          <View style={S.resCard}>
            <View style={S.resCardTop}>
              <View>
                <Text style={S.resCardTopLabel}>RESIDENT</Text>
                <Text style={S.resCardTopName}>{residentName || "—"}</Text>
              </View>
              {str(clientData?.levelOfCare) ? (
                <View style={S.resCardBadge}>
                  <Text style={S.resCardBadgeText}>
                    {str(clientData.levelOfCare)}
                  </Text>
                </View>
              ) : null}
            </View>

            <View style={S.resCardGrid1}>
              <View style={S.resCardCell}>
                <Text style={S.resCardCellLabel}>PLAN PERIOD</Text>
                <Text style={S.resCardCellValue}>{dateRange}</Text>
              </View>
              <View style={S.resCardCell}>
                <Text style={S.resCardCellLabel}>DATE OF BIRTH</Text>
                <Text style={S.resCardCellValue}>
                  {clientData?.dateOfBirth
                    ? moment(clientData.dateOfBirth).format("MM/DD/YYYY")
                    : "—"}
                </Text>
              </View>
              <View style={S.resCardCellLast}>
                <Text style={S.resCardCellLabel}>ADMISSION DATE</Text>
                <Text style={S.resCardCellValue}>
                  {clientData?.admissionDate
                    ? moment(clientData.admissionDate).format("MM/DD/YYYY")
                    : "—"}
                </Text>
              </View>
            </View>

            <View style={S.resCardGrid2}>
              <View style={S.resCardCell}>
                <Text style={S.resCardCellLabel}>CODE STATUS</Text>
                <Text style={S.resCardCellValue}>
                  {str(clientData?.codeStatus) || "—"}
                </Text>
              </View>
              <View style={S.resCardCell}>
                <Text style={S.resCardCellLabel}>ASSESSMENT TYPE</Text>
                <Text style={S.resCardCellValue}>
                  {str(clientData?.assessmentType) || "—"}
                </Text>
              </View>
              <View style={S.resCardCellLast}>
                <Text style={S.resCardCellLabel}>LEVEL OF CARE</Text>
                <Text style={S.resCardCellValue}>
                  {str(clientData?.levelOfCare) || "—"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <PDFPageFooter facilityName={facilityName} facilityAddress={facilityAddress} />
      </Page>

      {/* ══════ Content Pages ══════ */}
      <Page size="A4" style={S.page} wrap>
        <PDFPageFooter facilityName={facilityName} facilityAddress={facilityAddress} />

        {/* ── 01 Resident & Intake Data ── */}
        <PDFSectionHeading num="01" title="Resident & Intake Data" />

        <View style={S.fieldRow}>
          <PDFField label="First Name" value={clientData?.firstName} />
          <PDFField label="Last Name" value={clientData?.lastName} />
          <PDFField
            label="Date of Birth"
            value={
              clientData?.dateOfBirth
                ? moment(clientData.dateOfBirth).format("MM/DD/YYYY")
                : null
            }
          />
          <PDFField
            label="Admission Date"
            value={
              clientData?.admissionDate
                ? moment(clientData.admissionDate).format("MM/DD/YYYY")
                : null
            }
          />
        </View>

        <View style={S.fieldRow}>
          <PDFField label="Code Status" value={clientData?.codeStatus} />
          <PDFField label="Level of Care" value={clientData?.levelOfCare} />
          <PDFField label="Assessment Type" value={clientData?.assessmentType} />
          <PDFField
            label="Last TB Screening"
            value={
              clientData?.dateOfLastTBScreening
                ? moment(clientData.dateOfLastTBScreening).format("MM/DD/YYYY")
                : null
            }
          />
        </View>

        {/* Vital Signs */}
        <PDFDotHead title="Vital Signs — at Intake" />
        <View style={S.vitalTable}>
          <View style={S.vitalRowTop}>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>WEIGHT</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.weightLbs)
                  ? `${str(clientData.weightLbs)} lb`
                  : "—"}
              </Text>
            </View>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>BLOOD PRESSURE</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.bloodPressure) || "—"}
              </Text>
            </View>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>PULSE</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.pulse) || "—"}
              </Text>
            </View>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>OXYGEN</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.oxygen) || "—"}
              </Text>
            </View>
            <View style={S.vitalCellEnd}>
              <Text style={S.vitalCellLabel}>RESPIRATION</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.respiration) || "—"}
              </Text>
            </View>
          </View>
          <View style={S.vitalRowBottom}>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>TEMPERATURE</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.temperature) || "—"}
              </Text>
            </View>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>GLUCOSE</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.glucoseCheck) || "—"}
              </Text>
            </View>
            <View style={S.vitalCell}>
              <Text style={S.vitalCellLabel}>CHECK FREQ.</Text>
              <Text style={S.vitalCellValue}>
                {str(clientData?.frequency) || "—"}
              </Text>
            </View>
            <View style={S.vitalCellGray} />
          </View>
        </View>

        {/* Diagnosis + Allergies */}
        <View style={S.diagAllergyRow}>
          <View style={S.diagBlock}>
            <Text style={S.diagBlockLabel}>DIAGNOSES</Text>
            {clientData?.diagnosis?.length > 0 ? (
              clientData.diagnosis.map((d, i) => (
                <View key={i} style={S.diagDotRow}>
                  <View style={S.diagDot} />
                  <Text style={S.diagItemText}>{str(d)}</Text>
                </View>
              ))
            ) : (
              <Text style={S.fieldPlaceholder}>—</Text>
            )}
          </View>
          <View style={S.allergyBlock}>
            <Text style={S.allergyBlockLabel}>ALLERGIES</Text>
            {clientData?.allergies?.length > 0 ? (
              clientData.allergies.map((a, i) => (
                <Text key={i} style={S.allergyItemText}>
                  {str(a)}
                </Text>
              ))
            ) : (
              <Text style={S.fieldPlaceholder}>—</Text>
            )}
          </View>
        </View>

        {/* Clinical Summary */}
        {str(clientData?.otherHistory) ? (
          <View style={S.noteBlock}>
            <Text style={S.noteBlockLabel}>CLINICAL SUMMARY</Text>
            <Text style={S.noteBlockText}>{str(clientData.otherHistory)}</Text>
          </View>
        ) : null}

        {/* Contacts & Care Team */}
        <PDFDotHead title="Contacts & Care Team" />

        <View style={S.contactRow}>
          {str(clientData?.poaName) ? (
            <View style={S.contactCard}>
              <Text style={S.contactRoleLabel}>
                {str(clientData?.relationship)
                  ? str(clientData.relationship).toUpperCase()
                  : "POA / REPRESENTATIVE"}
              </Text>
              <Text style={S.contactName}>{str(clientData.poaName)}</Text>
              {str(clientData?.poaPhone) ? (
                <Text style={S.contactDetail}>{str(clientData.poaPhone)}</Text>
              ) : null}
            </View>
          ) : null}
          {str(clientData?.primaryCarePhysician) ? (
            <View style={S.contactCard}>
              <Text style={S.contactRoleLabel}>PRIMARY CARE PHYSICIAN</Text>
              <Text style={S.contactName}>
                {str(clientData.primaryCarePhysician)}
              </Text>
              {str(clientData?.practiceName) ? (
                <Text style={S.contactDetail}>
                  {str(clientData.practiceName)}
                </Text>
              ) : null}
              {str(clientData?.pcpPhone) ? (
                <Text style={S.contactDetail}>{str(clientData.pcpPhone)}</Text>
              ) : null}
            </View>
          ) : null}
        </View>

        <View style={S.contactRow}>
          {str(clientData?.pharmacyName) ? (
            <View style={S.contactCard}>
              <Text style={S.contactRoleLabel}>PHARMACY</Text>
              <Text style={S.contactName}>{str(clientData.pharmacyName)}</Text>
              {str(clientData?.pharmacyPhone) ? (
                <Text style={S.contactDetail}>
                  {str(clientData.pharmacyPhone)}
                </Text>
              ) : null}
            </View>
          ) : null}
          {str(clientData?.hospiceAgency) ? (
            <View style={S.contactCard}>
              <Text style={S.contactRoleLabel}>HOSPICE / HOME HEALTH</Text>
              <Text style={S.contactName}>{str(clientData.hospiceAgency)}</Text>
              {str(clientData?.hospicePhone) ? (
                <Text style={S.contactDetail}>
                  {str(clientData.hospicePhone)}
                </Text>
              ) : null}
            </View>
          ) : null}
          {str(clientData?.homeHealthAgency) ? (
            <View style={S.contactCard}>
              <Text style={S.contactRoleLabel}>HOME HEALTH AGENCY</Text>
              <Text style={S.contactName}>
                {str(clientData.homeHealthAgency)}
              </Text>
              {str(clientData?.servicesReceived) ? (
                <Text style={S.contactDetail}>
                  {str(clientData.servicesReceived)}
                </Text>
              ) : null}
              {str(clientData?.homeHealthAgencyPhone) ? (
                <Text style={S.contactDetail}>
                  {str(clientData.homeHealthAgencyPhone)}
                </Text>
              ) : null}
            </View>
          ) : null}
        </View>

        {str(clientData?.others) ? (
          <View style={S.noteBlock}>
            <Text style={S.noteBlockLabel}>NOTES</Text>
            <Text style={S.noteBlockText}>{str(clientData.others)}</Text>
          </View>
        ) : null}

        {/* ── 02 Physical Assessment ── */}
        <PDFSectionHeading num="02" title="Physical Assessment" />

        <PDFCheckboxSection
          title="Nutrition & Hydration"
          itemsDef={[
            ...mealLocationItems,
            ...nutritionItems,
            ...feedingAssistanceItems,
            ...hydrationItems,
          ]}
          selected={[
            ...(assessment?.mealsLocation || []),
            ...(assessment?.nutrition || []),
            ...(assessment?.feedingAssistance || []),
            ...(assessment?.hydration || []),
          ]}
        />
        <PDFCheckboxSection
          title="Assistive Devices"
          itemsDef={assistiveDevicesItems}
          selected={assessment?.assistiveDevices}
        />

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Vision"
            itemsDef={visionItems}
            selected={assessment?.vision}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Hearing"
            itemsDef={hearingItems}
            selected={assessment?.hearing}
            halfWidth
            columns={1}
          />
        </View>

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Speech"
            itemsDef={speechItems}
            selected={assessment?.speech}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Toileting & Continence"
            itemsDef={toiletingItems}
            selected={assessment?.toileting}
            halfWidth
            columns={1}
          />
        </View>

        {str(assessment?.physicalAssessmentNotes) ? (
          <View style={S.noteBlock}>
            <Text style={S.noteBlockLabel}>NOTES</Text>
            <Text style={S.noteBlockText}>
              {str(assessment.physicalAssessmentNotes)}
            </Text>
          </View>
        ) : null}

        {/* ── 03 Occupational Assessment ── */}
        <PDFSectionHeading num="03" title="Occupational Assessment" />

        <PDFCheckboxSection
          title="Oral Care & Brushing Teeth"
          itemsDef={brushingTeethItems}
          selected={assessment?.brushingTeeth}
        />

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Bed Check"
            itemsDef={bedCheckItems}
            selected={assessment?.bedCheck}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Skin Care"
            itemsDef={skinCareItems}
            selected={assessment?.skinCare}
            halfWidth
            columns={1}
          />
        </View>

        <PDFCheckboxSection
          title="Nutrition & Hydration"
          itemsDef={nutritionAndHydrationItems}
          selected={assessment?.nutritionAndHydration}
        />

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Undergarments & Briefs"
            itemsDef={undergarmentItems}
            selected={assessment?.undergarments}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Dressing"
            itemsDef={dressingItems}
            selected={assessment?.dressing}
            halfWidth
            columns={1}
          />
        </View>

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Grooming"
            itemsDef={groomingItems}
            selected={assessment?.grooming}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Bathing & Shower"
            itemsDef={showerItems}
            selected={assessment?.shower}
            halfWidth
            columns={1}
          />
        </View>

        <PDFCheckboxSection
          title="Other"
          itemsDef={otherItems}
          selected={assessment?.other}
        />

        <Text style={S.notesCardLabel}>NOTES</Text>
        <View style={S.notesCardBox}>
          <Text style={S.notesCardText}>
            {str(assessment?.occupationalAssessmentNotes) || "—"}
          </Text>
        </View>

        {/* ── 04 Activities of Daily Living ── */}
        <PDFSectionHeading num="04" title="Activities of Daily Living" />

        {taskTypeDetails?.subTasks?.map((subTask, index) => {
          const itemsDef = buildAdlItemsDef(subTask);
          const selected = buildAdlSelected(adl?.fieldsDone, subTask);
          return (
            <PDFCheckboxSection
              key={subTask?._id || index}
              title={subTask?.task?.name || ""}
              itemsDef={itemsDef}
              selected={selected}
            />
          );
        })}

        {/* ── 05 Care and Safety ── */}
        <PDFSectionHeading num="05" title="Care and Safety" />

        <PDFCheckboxSection
          title="Safety Monitoring"
          itemsDef={safetyMonitoringItems}
          selected={careAndSafety?.safetyMonitoring}
        />
        <PDFCheckboxSection
          title="Cognitive Status"
          itemsDef={cognitiveStatusItems}
          selected={careAndSafety?.cognitiveStatus}
        />

        <View style={S.cbCardRow}>
          <PDFCheckboxSection
            title="Engagement & Activities"
            itemsDef={cognitiveActivitiesItems}
            selected={careAndSafety?.cognitiveActivities}
            halfWidth
            columns={1}
          />
          <PDFCheckboxSection
            title="Coordination of Care"
            itemsDef={[...coordinationOfCareItems, ...familyRepCommunicationItems]}
            selected={[
              ...(careAndSafety?.coordinationOfCare || []),
              ...(careAndSafety?.familyRepCommunication || []),
            ]}
            halfWidth
            columns={1}
          />
        </View>

        <PDFCheckboxSection
          title="Medication Administration"
          itemsDef={medicationAdministrationItems}
          selected={careAndSafety?.medicationAdministration}
        />
        <PDFCheckboxSection
          title="Mobility & Transfers"
          itemsDef={mobilityAmbulationTransferItems}
          selected={careAndSafety?.mobilityAmbulationTransfer}
        />
        <PDFCheckboxSection
          title="Nursing & Medical Instructions"
          itemsDef={nursingMedicalInstructionsItems}
          selected={careAndSafety?.nursingMedicalInstructions}
        />

        {/* Notify Physician If */}
        {selectedConditions.length > 0 ? (
          <View style={S.notifyBox}>
            <Text style={S.notifyBoxLabel}>NOTIFY PHYSICIAN IF</Text>
            <View style={S.notifyGrid}>
              {selectedConditions.map((item, i) => (
                <View key={i} style={S.notifyItem}>
                  <View style={S.notifyBullet} />
                  <Text style={S.notifyText}>{item?.name || str(item)}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : conditionsRequiringCaregiversItems?.length > 0 ? (
          <View style={S.notifyBox}>
            <Text style={S.notifyBoxLabel}>NOTIFY PHYSICIAN IF</Text>
            <View style={S.notifyGrid}>
              {conditionsRequiringCaregiversItems.map((item, i) => (
                <View key={i} style={S.notifyItem}>
                  <View style={S.notifyBullet} />
                  <Text style={S.notifyText}>{item.name}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}
      </Page>

      {/* ══════ Signatures Page ══════ */}
      {signature.length > 0 && (
        <Page size="A4" style={S.page}>
          <PDFPageFooter facilityName={facilityName} facilityAddress={facilityAddress} />

          <PDFSectionHeading num="06" title="Review & Signatures" />

          <Text style={S.sigParagraph}>
            This Service Plan has been developed collaboratively with the
            resident and/or their representative, the care team, and the
            attending physician. By signing below, each party affirms that the
            assessed needs and the plan of care described in this document have
            been reviewed, discussed, and agreed upon.
          </Text>

          <View style={S.reviewDatesRow}>
            <View style={S.reviewDateCard}>
              <Text style={S.reviewDateLabel}>PLAN EFFECTIVE</Text>
              <Text style={S.reviewDateValue}>
                {planPayload?.startDate
                  ? moment(planPayload.startDate).format("MMMM D, YYYY")
                  : "—"}
              </Text>
            </View>
            <View style={S.reviewDateCard}>
              <Text style={S.reviewDateLabel}>PLAN PERIOD</Text>
              <Text style={S.reviewDateValue}>{dateRange}</Text>
            </View>
          </View>

          {email
            ? signature
                .filter((sig) => sig?.email === email)
                .map((sig, i) => (
                  <PDFSignatureRow key={sig?.id || i} sig={sig} />
                ))
            : signature
                .filter((sig) => sig?.signatureRequestStatus === "success")
                .map((sig, i) => (
                  <PDFSignatureRow key={sig?.id || i} sig={sig} />
                ))}

          {/* Post-signature confidentiality banner */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 24,
              borderTop: "0.5 solid #E5E7EB",
              paddingTop: 10,
            }}
          >
            <Image
              src="/assets/images/logo2.png"
              style={{ width: 22, height: 22, objectFit: "contain" }}
            />
            <View style={{ alignItems: "flex-end" }}>
              <Text style={{ fontSize: 7, color: "#9CA3AF", letterSpacing: 0.3 }}>
                CONFIDENTIAL — PROTECTED HEALTH INFORMATION
              </Text>
              {facilityName ? (
                <Text style={{ fontSize: 7, color: "#9CA3AF", letterSpacing: 0.3, marginTop: 2 }}>
                  {facilityName.toUpperCase()}
                </Text>
              ) : null}
            </View>
          </View>
        </Page>
      )}
    </Document>
  );
};

// ─── Exports ──────────────────────────────────────────────────────────────────

export const ServicePlanPDFPreview = (props) => (
  <PDFViewer style={{ width: "100%", height: 620, border: "none" }}>
    <ServicePlanDocument {...props} />
  </PDFViewer>
);

export const getServicePlanBlob = async (props) => {
  const blob = await pdf(<ServicePlanDocument {...props} />).toBlob();
  return blob;
};

export const downloadServicePlanPDF = async (
  planPayload,
  taskTypeDetails,
  fileName = "service-plan.pdf",
) => {
  const blob = await getServicePlanBlob(planPayload, taskTypeDetails);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export default ServicePlanDocument;
